import axios from 'axios';
import axiosInstance from '../auth/axiosInstance';
import { getApiBaseUrl } from '../auth/apiBaseUrl';

const API_URL = getApiBaseUrl();

function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    if (typeof data?.message === 'string' && data.message) {
      return data.message;
    }
    if (data?.errors && typeof data.errors === 'object') {
      const first = Object.values(data.errors as Record<string, string[]>)[0];
      if (Array.isArray(first) && first[0]) {
        return first[0];
      }
    }
    if (typeof data?.title === 'string' && data.title) {
      return data.title;
    }
  }

  return error instanceof Error ? error.message : 'Request failed';
}

function assertApiSuccess<T>(data: {
  data?: T;
  succeeded?: boolean;
  message?: string;
  code?: number;
}): T {
  const failed =
    data?.succeeded === false ||
    (typeof data?.code === 'number' && data.code >= 400);

  if (failed) {
    throw new Error(data?.message || 'Request failed');
  }

  return data.data as T;
}

export interface ValidateInvitationResponse {
  isValid: boolean;
  errorCode?: string;
  projectDisplayName?: string;
  isEmailLocked?: boolean;
  emailAddress?: string;
  emailHint?: string;
  accountExists?: boolean;
  expiresAt?: string;
  roleDisplayName?: string;
}

export interface ProjectInvitationListItem {
  invitationId: number;
  emailAddress?: string;
  roleId?: number;
  roleName?: string;
  status: string;
  expiresAt?: string;
  createdAt: string;
  consumedAt?: string;
  lastEmailSentAt?: string;
  emailSendCount: number;
  createdByName: string;
}

export interface CreateInvitationResult {
  invitationId: number;
  inviteUrl: string;
  emailAddress?: string;
  status: string;
  expiresAt?: string;
  createdAt: string;
  emailSent?: boolean;
  emailWarning?: string;
}

export type InvitationRoleName = 'Client' | 'SuperUser';

export const validateInvitation = async (
  token: string
): Promise<ValidateInvitationResponse> => {
  const { data } = await axios.get(`${API_URL}/Invitation/validate`, {
    params: { token },
  });

  return data.data ?? data;
};

export const listProjectInvitations = async (
  projectId: number
): Promise<ProjectInvitationListItem[]> => {
  const { data } = await axiosInstance.get(`/Invitation/project/${projectId}`);
  return data.data ?? [];
};

export const createProjectInvitations = async (payload: {
  projectId: number;
  emailAddresses?: string[];
  expiryPolicy?: 'SevenDays' | 'ThirtyDays' | 'Never';
  sendEmail?: boolean;
  roleName?: InvitationRoleName;
}): Promise<CreateInvitationResult[]> => {
  if (!payload.projectId) {
    throw new Error('Project is missing. Close the dialog and try again.');
  }

  try {
    const { data } = await axiosInstance.post('/Invitation', payload);
    return assertApiSuccess<CreateInvitationResult[]>(data) ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const regenerateInvitation = async (
  invitationId: number,
  payload: {
    expiryPolicy?: 'SevenDays' | 'ThirtyDays' | 'Never';
    sendEmail?: boolean;
  } = {}
): Promise<CreateInvitationResult> => {
  try {
    const { data } = await axiosInstance.post(
      `/Invitation/${invitationId}/regenerate`,
      payload
    );
    return assertApiSuccess<CreateInvitationResult>(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const copyTextToClipboard = async (text: string): Promise<void> => {
  await navigator.clipboard.writeText(text);
};
